import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Copy, Lock, Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react'
import clsx from 'clsx'

import { crmErrorDetail, crmListService, type CrmContactList } from '@/services/crm'
import { ActionMenu, MenuConfirm, MenuItem } from './ActionMenu'
import ListEditorDialog from './ListEditorDialog'
import { listColourClass, orderLists } from './crmFormat'

/**
 * One row of chips: All · People · Companies · Brokers · Borrowers ·
 * Investors · Internal · ‹the desk's own lists› · + New list.
 *
 * `variant="rail"` lays the very same chips out as a vertical list for the
 * relationships page's left rail: every list is shown (no overflow menu), the
 * count sits at the right edge, and the options menu, rename, duplicate,
 * delete and rules editor behave exactly as they do in the row.
 *
 * The categories and the user's lists are the same kind of thing — a rule the
 * server evaluates — so they sit on one row and are managed the same way. The
 * old page hard-coded five tabs on the left and kept the lists in a sidebar
 * on the far side of the screen; a person looking for "Brokers I like" had
 * to know which of the two to look in.
 *
 * The row never wraps and never overflows the page. Chips are measured in a
 * hidden copy of the row, and as many as fit are shown; the rest collapse
 * into a "More ▾" menu. Where ResizeObserver does not exist (jsdom) every
 * chip is shown.
 */
const GAP = 6
const MORE_RESERVE = 84

export default function ListBar({
  activeListId,
  onSelectList,
  totalCount,
  variant = 'row',
}: {
  activeListId: string | null
  onSelectList: (id: string | null) => void
  /** How many contacts "All" holds, from the page's own roster. */
  totalCount: number | null
  variant?: 'row' | 'rail'
}) {
  const rail = variant === 'rail'
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<CrmContactList | 'new' | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; value: string; error: string | null } | null>(null)
  const [note, setNote] = useState<{ id: string; text: string } | null>(null)

  const lists = useQuery({
    queryKey: ['crm', 'lists'],
    queryFn: crmListService.list,
    staleTime: 30_000,
  })
  const ordered = useMemo(() => orderLists(lists.data?.lists ?? []), [lists.data])

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
    ])

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => crmListService.update(id, { name }),
    onSuccess: async () => {
      setRenaming(null)
      await refresh()
    },
    onError: (failure, variables) =>
      setRenaming((current) =>
        current && current.id === variables.id
          ? { ...current, error: crmErrorDetail(failure, 'The list could not be renamed.') }
          : current,
      ),
  })

  const duplicate = useMutation({
    mutationFn: (list: CrmContactList) =>
      crmListService.create({
        name: `${list.name} (copy)`,
        description: list.description ?? '',
        match: list.match,
        rules: list.rules,
        ...(list.colour ? { colour: list.colour } : {}),
      }),
    onSuccess: async (created) => {
      await refresh()
      onSelectList(created.id)
    },
    onError: (failure, list) =>
      setNote({ id: list.id, text: crmErrorDetail(failure, 'The list could not be duplicated.') }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => crmListService.remove(id),
    onSuccess: async (_result, id) => {
      if (activeListId === id) onSelectList(null)
      await refresh()
    },
    onError: (failure, id) => setNote({ id, text: crmErrorDetail(failure, 'The list could not be deleted.') }),
  })

  // ── Overflow: measure every chip in a hidden row, show as many as fit ──
  const rowRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [rowWidth, setRowWidth] = useState<number | null>(null)
  const [visibleCount, setVisibleCount] = useState<number>(Number.POSITIVE_INFINITY)
  const canMeasure = !rail && typeof ResizeObserver !== 'undefined'

  useEffect(() => {
    if (!canMeasure || !rowRef.current) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (typeof width === 'number') setRowWidth(width)
    })
    observer.observe(rowRef.current)
    return () => observer.disconnect()
  }, [canMeasure])

  // An inline rename field, or a refusal shown beside a chip, widens that
  // chip beyond what the hidden copy measured; room is set aside for it
  // while it shows so the row does not push "+ New list" out of sight.
  const transient = renaming?.error || note ? 240 : renaming ? 120 : 0

  useLayoutEffect(() => {
    if (!canMeasure || rowWidth === null || !measureRef.current) return
    const clones = Array.from(measureRef.current.children) as HTMLElement[]
    // The last clone is "+ New list", which always shows.
    const newList = clones[clones.length - 1]?.offsetWidth ?? 0
    const chips = clones.slice(0, -1)
    const room = rowWidth - transient
    const total = chips.reduce((sum, el) => sum + el.offsetWidth + GAP, 0) + newList
    if (total <= room) {
      setVisibleCount(Number.POSITIVE_INFINITY)
      return
    }
    let used = newList + MORE_RESERVE
    let count = 0
    for (const chip of chips) {
      if (used + chip.offsetWidth + GAP > room) break
      used += chip.offsetWidth + GAP
      count += 1
    }
    setVisibleCount(count)
  }, [canMeasure, rowWidth, ordered, totalCount, transient])

  // "All" is chip 0; the active chip is always among the visible ones, at
  // the cost of the last one that would otherwise fit.
  const entries: (CrmContactList | null)[] = [null, ...ordered]
  let visible = entries
  let overflow: CrmContactList[] = []
  if (Number.isFinite(visibleCount) && visibleCount < entries.length) {
    const count = Math.max(1, visibleCount)
    visible = entries.slice(0, count)
    overflow = entries.slice(count).filter((e): e is CrmContactList => e !== null)
    const activeHidden = overflow.find((l) => l.id === activeListId)
    if (activeHidden) {
      const dropped = visible[visible.length - 1]
      visible = [...visible.slice(0, -1), activeHidden]
      overflow = overflow.filter((l) => l.id !== activeHidden.id)
      if (dropped) overflow = [dropped, ...overflow]
    }
  }

  function startRename(list: CrmContactList) {
    setRenaming({ id: list.id, value: list.name, error: null })
  }

  function onRenameKey(event: KeyboardEvent<HTMLInputElement>, list: CrmContactList) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const value = renaming?.value.trim() ?? ''
      if (!value || value === list.name) {
        setRenaming(null)
        return
      }
      rename.mutate({ id: list.id, name: value })
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setRenaming(null)
    }
  }

  return (
    <div
      ref={rowRef}
      className={clsx(
        'relative flex min-w-0 gap-1.5',
        rail ? 'flex-col items-stretch gap-0.5' : 'items-center overflow-hidden',
      )}
    >
      {/* The hidden copy the row is measured against. */}
      {rail ? null : (
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex h-0 items-center gap-1.5 overflow-hidden whitespace-nowrap"
      >
        {/* Each clone mirrors the real chip's boxes exactly — the label
            button, then the 12px menu glyph with its right padding — because
            a clone that is wider than its chip hides a list that would have
            fit. The first cut of this measured a "w-4" spacer against a 16px
            menu and folded two lists into More on a 1920px screen. */}
        {entries.map((list) => (
          <span
            key={list?.id ?? 'all'}
            className="flex flex-shrink-0 items-center whitespace-nowrap rounded-md border border-transparent"
          >
            <span className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium">
              <ChipLabel list={list} totalCount={totalCount} />
            </span>
            {list ? (
              <span className="flex items-center pr-1">
                <span className="block h-3 w-3" />
              </span>
            ) : null}
          </span>
        ))}
        <span className={chipClass(false)}>
          <Plus size={11} /> New list
        </span>
      </div>
      )}

      {lists.isLoading ? <span className="text-[10px] text-stone-500">Reading lists…</span> : null}
      {lists.isError ? (
        <span className="flex items-center gap-2 text-[10px] text-red-300">
          The lists could not be read.
          <button type="button" onClick={() => lists.refetch()} className="underline hover:text-red-200">
            Retry
          </button>
        </span>
      ) : null}

      {!lists.isLoading && !lists.isError
        ? visible.map((list) => {
            const id = list?.id ?? null
            const active = activeListId === id
            const isRenaming = list !== null && renaming?.id === list.id
            return (
              <div
                key={id ?? 'all'}
                className={clsx(
                  'group relative flex flex-shrink-0 items-center whitespace-nowrap rounded-md border',
                  rail && 'w-full',
                  active
                    ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200'
                    : 'border-transparent text-stone-400 hover:border-amber-400/15 hover:bg-amber-400/[0.04] hover:text-stone-200',
                )}
              >
                {isRenaming && list ? (
                  <span className="flex items-center px-1.5 py-0.5">
                    <input
                      autoFocus
                      value={renaming?.value ?? ''}
                      aria-label={`Rename ${list.name}`}
                      onChange={(event) =>
                        setRenaming((current) => (current ? { ...current, value: event.target.value, error: null } : current))
                      }
                      onKeyDown={(event) => onRenameKey(event, list)}
                      onBlur={() => {
                        // A blur while the save is in flight, or after an
                        // error, keeps the field so the message can be read.
                        if (!rename.isPending && !renaming?.error) setRenaming(null)
                      }}
                      className="w-32 rounded border border-amber-400/30 bg-black/60 px-1.5 py-0.5 text-[11px] text-stone-100 outline-none"
                    />
                    {/* Inline, not floated below: the row clips anything
                        outside it, and a message nobody can see is no
                        message. The chip widens for as long as it shows. */}
                    {renaming?.error ? (
                      <span role="alert" className="ml-1.5 max-w-[220px] truncate text-[10px] text-red-300" title={renaming.error}>
                        {renaming.error}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-pressed={active}
                    title={list?.summary ?? 'Every contact in the directory'}
                    onClick={() => onSelectList(id)}
                    className={clsx(
                      'flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
                      rail && 'min-w-0 flex-1',
                    )}
                  >
                    <ChipLabel list={list} totalCount={totalCount} rail={rail} />
                  </button>
                )}
                {list ? (
                  <span
                    className={clsx(
                      'flex items-center pr-1',
                      !active && 'opacity-0 focus-within:opacity-100 group-hover:opacity-100',
                    )}
                  >
                    <ActionMenu label={`Options for ${list.name}`} size={12}>
                      {(close) => (
                        <ListMenu
                          list={list}
                          onRules={() => {
                            close()
                            setEditing(list)
                          }}
                          onRename={() => {
                            close()
                            startRename(list)
                          }}
                          onDuplicate={() => {
                            close()
                            duplicate.mutate(list)
                          }}
                          onDelete={() => remove.mutate(list.id, { onSuccess: close })}
                          deleting={remove.isPending && remove.variables === list.id}
                        />
                      )}
                    </ActionMenu>
                  </span>
                ) : null}
                {note && list && note.id === list.id ? (
                  <span role="alert" className="flex items-center gap-1 pr-1.5 text-[10px] text-red-300">
                    <span className="max-w-[220px] truncate" title={note.text}>
                      {note.text}
                    </span>
                    <button type="button" onClick={() => setNote(null)} className="text-stone-400 hover:text-stone-200" aria-label="Dismiss">
                      ×
                    </button>
                  </span>
                ) : null}
              </div>
            )
          })
        : null}

      {overflow.length > 0 ? (
        <ActionMenu
          label="More lists"
          align="start"
          className={chipClass(overflow.some((l) => l.id === activeListId))}
          trigger={
            <span className="flex items-center gap-1 text-[11px] font-medium">
              More <ChevronDown size={11} />
              <span className="font-mono text-[10px] text-stone-500">{overflow.length}</span>
            </span>
          }
        >
          {(close) => (
            <>
              {overflow.map((list) => (
                <MenuItem
                  key={list.id}
                  onSelect={() => {
                    close()
                    onSelectList(list.id)
                  }}
                  icon={list.isSystem ? <Lock size={10} /> : undefined}
                >
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{list.name}</span>
                    <span className="font-mono text-[10px] text-stone-500">{list.memberCount}</span>
                  </span>
                </MenuItem>
              ))}
            </>
          )}
        </ActionMenu>
      ) : null}

      {!lists.isLoading && !lists.isError ? (
        <button
          type="button"
          onClick={() => setEditing('new')}
          className={clsx(
            'flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-dashed border-amber-400/15 px-2 py-1 text-[11px] text-amber-300 hover:border-amber-400/30 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
            rail && 'mt-1 w-full justify-start',
          )}
        >
          <Plus size={11} /> New list
        </button>
      ) : null}

      {editing && lists.data ? (
        <ListEditorDialog
          list={editing === 'new' ? null : editing}
          vocabulary={lists.data}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}

function chipClass(active: boolean): string {
  return clsx(
    'flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-medium',
    active
      ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200'
      : 'border-transparent text-stone-400 hover:border-amber-400/15 hover:bg-amber-400/[0.04] hover:text-stone-200',
  )
}

function ChipLabel({
  list,
  totalCount,
  rail = false,
}: {
  list: CrmContactList | null
  totalCount: number | null
  rail?: boolean
}) {
  const colour = listColourClass(list?.colour)
  return (
    <>
      {colour ? <span className={clsx('h-1.5 w-1.5 rounded-full', colour)} aria-hidden="true" /> : null}
      <span className={clsx(rail && 'min-w-0 truncate')}>{list?.name ?? 'All'}</span>
      <span className={clsx('font-mono text-[10px] text-stone-500', rail && 'ml-auto')}>
        {list ? list.memberCount : totalCount === null ? '…' : totalCount}
      </span>
      {list?.isSystem ? <Lock size={9} className="text-stone-600" aria-label="Built in" /> : null}
    </>
  )
}

function ListMenu({
  list,
  onRules,
  onRename,
  onDuplicate,
  onDelete,
  deleting,
}: {
  list: CrmContactList
  onRules: () => void
  onRename: () => void
  onDuplicate: () => void
  onDelete: () => void
  deleting: boolean
}) {
  const [confirming, setConfirming] = useState(false)
  if (confirming) {
    return (
      <MenuConfirm
        question={`Delete ${list.name}?`}
        confirmLabel="Yes, delete"
        pending={deleting}
        onConfirm={onDelete}
        onCancel={() => setConfirming(false)}
      />
    )
  }
  return (
    <>
      <MenuItem icon={<SlidersHorizontal size={11} />} onSelect={onRules}>
        Rules…
      </MenuItem>
      {/* A built-in list keeps its name and cannot be deleted — its name is
          the vocabulary the directory is filed under. Its rules are its
          owner's to change. */}
      {!list.isSystem ? (
        <>
          <MenuItem icon={<Pencil size={11} />} onSelect={onRename}>
            Rename
          </MenuItem>
          <MenuItem icon={<Copy size={11} />} onSelect={onDuplicate}>
            Duplicate
          </MenuItem>
          <MenuItem icon={<Trash2 size={11} />} danger onSelect={() => setConfirming(true)}>
            Delete…
          </MenuItem>
        </>
      ) : null}
    </>
  )
}
